import React, { useEffect, useState } from 'react';
import { X, Sparkles, Zap, Crown, Loader2, Coins } from 'lucide-react';
import { axiosAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';

const ICONS = { starter: Sparkles, fan: Zap, vip: Crown };

const BuyCreditsModal = () => {
  const { showBuyModal, closeBuyModal, insufficientFor, credits, costTrack, costBookAudio } = useCredits();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    if (!showBuyModal) return;
    setLoading(true);
    axiosAuth.get('/api/credits/packs')
      .then(({ data }) => setPacks(data))
      .catch(() => setPacks([]))
      .finally(() => setLoading(false));
  }, [showBuyModal]);

  // Lock body scroll while the modal is open so the page behind doesn't
  // capture scroll gestures on mobile (and so closing always returns the
  // user exactly where they were).
  useEffect(() => {
    if (!showBuyModal) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    // Compensate for scrollbar width to prevent layout shift on desktop
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [showBuyModal]);

  // ESC to close
  useEffect(() => {
    if (!showBuyModal) return;
    const onKey = (e) => { if (e.key === 'Escape') closeBuyModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showBuyModal, closeBuyModal]);

  const handleBuy = async (packId) => {
    setCheckingOut(packId);
    try {
      const { data } = await axiosAuth.post('/api/credits/checkout', {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert('Could not start checkout. Please try again.');
      }
    } catch (err) {
      alert('Checkout failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCheckingOut(null);
    }
  };

  if (!showBuyModal) return null;

  // "Fan" is best value — highlight that pack
  const bestValueId = 'fan';

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-hidden"
      onClick={closeBuyModal}
      data-testid="buy-credits-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="buy-credits-title"
    >
      {/* Modal card — owns its own scroll, never grows larger than the viewport */}
      <div
        className="relative bg-[#0D0D0D] border border-[#D4AF37]/30 sm:rounded-lg max-w-3xl w-full h-full sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header — close button is ALWAYS reachable, no matter how far
            the user has scrolled inside the modal. */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 sm:px-8 py-3 bg-[#0D0D0D]/95 backdrop-blur border-b border-white/10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#D4AF37]">
            <Coins size={14} />
            Streaming Credits
          </div>
          <button
            onClick={closeBuyModal}
            className="flex items-center justify-center h-10 w-10 -mr-2 rounded-full text-[#A3A3A3] hover:text-white hover:bg-white/5 transition-colors"
            data-testid="buy-credits-close"
            aria-label="Close credit packs modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-8 py-6 sm:py-8">
          <div className="text-center mb-6">
            <h2 id="buy-credits-title" className="font-['Bebas_Neue'] text-3xl md:text-4xl mb-2">
              {insufficientFor
                ? 'Out of credits — top up to keep streaming'
                : 'Stream more. Listen longer.'}
            </h2>
          <p className="text-sm text-[#A3A3A3]">
            Each track stream costs <span className="text-[#D4AF37] font-medium">{costTrack} credits</span>.
            Each audiobook listen costs <span className="text-[#D4AF37] font-medium">{costBookAudio} credits</span>.
            Credits never expire. Owned tracks always stream free.
          </p>
          <p className="text-sm text-[#737373] mt-2">
            Current balance: <span className="text-white font-medium" data-testid="modal-current-balance">{credits}</span>
          </p>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <Loader2 size={28} className="animate-spin text-[#D4AF37] mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packs.map((p) => {
              const Icon = ICONS[p.id] || Sparkles;
              const isBest = p.id === bestValueId;
              return (
                <div
                  key={p.id}
                  className={`relative bg-[#1A1A1A] border ${
                    isBest ? 'border-[#D4AF37]' : 'border-white/10'
                  } rounded-lg p-6 text-center hover:border-[#D4AF37] transition-colors`}
                  data-testid={`credit-pack-${p.id}`}
                >
                  {isBest && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#D4AF37] text-black text-[10px] font-bold uppercase tracking-wider">
                      Best Value
                    </span>
                  )}
                  <Icon size={32} className="mx-auto text-[#D4AF37] mb-3" />
                  <p className="text-xs uppercase tracking-[0.18em] text-[#737373]">{p.label}</p>
                  <p className="font-['Bebas_Neue'] text-5xl text-white mt-2">{p.credits}</p>
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">credits</p>
                  <p className="font-['Bebas_Neue'] text-3xl text-[#D4AF37] mt-4">
                    ${p.price_usd.toFixed(2)}
                  </p>
                  <p className="text-[10px] text-[#737373] mt-1">
                    ≈ ${p.price_per_credit.toFixed(3)} per credit
                  </p>
                  <button
                    onClick={() => handleBuy(p.id)}
                    disabled={checkingOut === p.id}
                    className={`mt-6 w-full px-4 py-3 text-sm font-medium uppercase tracking-wider transition-colors inline-flex items-center justify-center gap-2 ${
                      isBest
                        ? 'bg-[#D4AF37] text-black hover:bg-[#E5C048]'
                        : 'border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10'
                    } disabled:opacity-60`}
                    data-testid={`buy-pack-${p.id}`}
                  >
                    {checkingOut === p.id ? <Loader2 size={14} className="animate-spin" /> : null}
                    {checkingOut === p.id ? 'Redirecting…' : `Buy ${p.label}`}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-6">
          Secure checkout via Stripe · Credits added instantly after payment
        </p>
        </div>{/* /scrollable body */}
      </div>{/* /modal card */}
    </div>
  );
};

export default BuyCreditsModal;
